import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Building2, Users, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";
import { payrollCompanies, payrollCreateCompany, formatApiError } from "@/lib/api";
import PayrollWorkspace from "@/components/payroll/PayrollWorkspace";

const inputCls = "w-full border border-steel-border bg-steel-elevated px-3 py-2 text-sm text-steel-text placeholder:text-steel-muted focus:border-steel-accent focus:outline-none rounded-none";

export default function PayrollTab({ adminKey }) {
  const [companies, setCompanies] = useState(null);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", ein: "", state: "MA", pay_frequency: "biweekly" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => payrollCompanies(adminKey).then((d) => setCompanies(d.companies)).catch(() => setCompanies([])), [adminKey]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Company name is required.");
    setBusy(true);
    try {
      const c = await payrollCreateCompany(adminKey, form);
      toast.success("Company created.");
      setCreating(false); setForm({ name: "", ein: "", state: "", pay_frequency: "biweekly" });
      await load(); setSelected(c.id);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (selected) {
    return (
      <div className="mt-8">
        <PayrollWorkspace adminKey={adminKey} companyId={selected} isAdmin onBack={() => { setSelected(null); load(); }} />
      </div>
    );
  }

  return (
    <div className="mt-8" data-testid="payroll-tab">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="font-display text-2xl font-medium text-steel-text">Payroll companies</h3>
          <p className="text-sm text-steel-muted">Set up each business, assign the client, and run comprehensive federal + state payroll.</p>
        </div>
        <button data-testid="payroll-new-company-btn" onClick={() => setCreating(true)} className="flex items-center gap-2 bg-steel-text px-5 py-2.5 text-sm font-semibold text-steel-bg hover:bg-zinc-300"><Plus className="h-4 w-4" /> New company</button>
      </div>

      {creating && (
        <div className="mb-6 border border-steel-border bg-steel-surface p-6" data-testid="payroll-create-form">
          <div className="mb-4 flex items-center justify-between"><h4 className="font-display text-lg text-steel-text">New payroll company</h4><button onClick={() => setCreating(false)}><X className="h-4 w-4 text-steel-muted" /></button></div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <input data-testid="company-name" className={inputCls} placeholder="Company name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input className={inputCls} placeholder="EIN (12-3456789)" value={form.ein} onChange={(e) => setForm({ ...form, ein: e.target.value })} />
            <input className={inputCls} placeholder="State (MA)" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
            <select className={inputCls} value={form.pay_frequency} onChange={(e) => setForm({ ...form, pay_frequency: e.target.value })}>
              <option value="weekly">Weekly</option><option value="biweekly">Bi-weekly</option><option value="semimonthly">Semi-monthly</option><option value="monthly">Monthly</option>
            </select>
          </div>
          <button data-testid="company-create-btn" onClick={create} disabled={busy} className="mt-4 flex items-center gap-2 bg-steel-text px-5 py-2.5 text-sm font-semibold text-steel-bg hover:bg-zinc-300 disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create & configure</button>
        </div>
      )}

      {!companies ? <Loader2 className="h-5 w-5 animate-spin text-steel-muted" /> :
        companies.length === 0 ? <p className="border border-dashed border-steel-border p-10 text-sm text-steel-muted">No payroll companies yet. Create the first one.</p> :
          <div className="space-y-3">
            {companies.map((c) => (
              <button key={c.id} data-testid={`payroll-company-${c.id}`} onClick={() => setSelected(c.id)} className="flex w-full items-center justify-between gap-4 border border-steel-border bg-steel-surface px-6 py-4 text-left hover:border-steel-brass/50">
                <div className="flex items-center gap-4">
                  <div className="flex h-11 w-11 items-center justify-center border border-steel-border"><Building2 className="h-5 w-5 text-steel-brass" /></div>
                  <div><p className="font-medium text-steel-text">{c.name}</p><p className="text-xs text-steel-muted">EIN {c.ein || "—"} · {c.state || "—"}</p></div>
                </div>
                <div className="flex items-center gap-4 text-sm text-steel-muted"><span className="flex items-center gap-1.5"><Users className="h-4 w-4" /> {c.employee_count}</span><ChevronRight className="h-4 w-4" /></div>
              </button>
            ))}
          </div>}
    </div>
  );
}
